import type { Entity } from '@core/types';
import { scanApiV1 as scanApi, scanCategoryApiV1 as scanCategoryApi } from '@deepup/apis';
import { OrderNumber } from '@projects/types/orderNumbers';

export interface ScanBoundingBox {
  latitudeMax: number;
  latitudeMin: number;
  longitudeMax: number;
  longitudeMin: number;
}

export interface ScanMetadata {
  beginDate?: string;
  boundingBox?: ScanBoundingBox;
  endDate?: string;
}

export interface ScanLocation {
  city?: string;
  latitudeMean: number;
  longitudeMean: number;
  place?: string;
}

export interface ScanCoordinate {
  latitude: number;
  longitude: number;
  altitude?: number;
}

export const reworkCategories = [
  'TRENCH_OR_ELEMENT_PLACEMENT',
  'ADDRESS',
  'CONNECTOR_OR_PIPE_FITTING',
  'SCAN_POSITION',
  'ELEMENT_NAME',
  'SURFACE_TYPE',
  'LAYING_TYPE',
  'WIDTH',
  'DEPTH',
  'POINT_OBJECT',
  'MARKER_SPHERE',
  'HOUSE_LEAD_VARIANT',
  'PARENT_NETWORK_NODE',
  'DOUBLE_CHECK_PHOTOS',
  'MISSING_ELEMENT',
  'PIPE_COLOUR',
  'PIPE_STRENGTH',
  'PROTECTIVE_PIPE',
] as const;

export type ReworkCategory = (typeof reworkCategories)[number];

// NOTE: this is a quick fix to avoid changing the existing data model.
// These categories are not shown in the rework status chip list if they are not selected and can't be checked.
// They stil exist in the backoffice db.
export const deletedToReworkCategories = [
  'DOUBLE_CHECK_PHOTOS',
  'MISSING_ELEMENT',
  'PIPE_COLOUR',
  'PIPE_STRENGTH',
] as const;

export type DeletedToReworkCategories = (typeof deletedToReworkCategories)[number];

export const qaStates = [
  'QA_READY',
  'DONE',
  'AUTO_DONE',
  'WAITING_FOR_PLAN_DATA_UPDATE',
  'TO_LABEL',
  'TO_REWORK',
  'TO_CLARIFY',
  'SCAN_ONLY_DONE',
] as const;
export type QAState = (typeof qaStates)[number];

export const qaStateDescriptions: Record<QAState, string> = {
  QA_READY: 'Ready for QA review',
  DONE: 'Completed',
  AUTO_DONE: 'Automatically completed',
  WAITING_FOR_PLAN_DATA_UPDATE:
    'Waiting for plan data update - can be labeled but missing plan data',
  TO_LABEL: 'Needs labeling',
  TO_REWORK: 'Needs rework',
  TO_CLARIFY: 'Needs clarification',
  SCAN_ONLY_DONE:
    'Unsupported QA state (SCAN_ONLY_DONE). This scan should not be in the back office. Please double check with engineering',
};

export type QaStateUpdate = {
  qaState: QAState;
  reworkCategories?: ReworkCategory[];
  id: string; // this is the scanId
};

export const surfaceClassificationCategories = ['NOT_CLASSIFIED', 'SURFACE', 'TRENCH'] as const;
export type SurfaceClassification = (typeof surfaceClassificationCategories)[number];

export const surfaceClassificationFeedbackReasons = {
  WRONG_CLASSIFICATION: 'Wrong class assignment',
  SOIL_DISPLACEMENT: 'Soil displacement',
  FLUSH_DRILLING: 'Flush drilling',
  OTHER: 'Other',
} as const;

export type SurfaceClassificationFeedbackReasons =
  keyof typeof surfaceClassificationFeedbackReasons;

export type SurfaceClassificationFeedback = {
  newClassification: Exclude<(typeof surfaceClassificationCategories)[number], 'NOT_CLASSIFIED'>;
  reason?: SurfaceClassificationFeedbackReasons;
};

export interface Scan extends Entity {
  detailsFetched?: boolean;
  disableUserTransformation?: boolean;
  disableAutoAlignedPosition?: boolean;
  externalScanId?: string;
  loaded?: boolean; // TODO since this prop doesn't directly belong to the data model but is a view prop of the scene consider putting loaded scanIds in an array prop of viewer3d slice.
  location?: ScanLocation;
  metadata: ScanMetadata;
  projectId?: string | null;
  selectedProcessingResultId?: string;
  tagIds: string[];
  uploadedAt?: string;
  visible: boolean;
  qaState: QAState;
  surfaceClassification: SurfaceClassification;
  reworkCategories: ReworkCategory[];
  scanCategory?: scanCategoryApi.ScanCategoryKind;
  deviceName?: string;
  orderNumbers?: OrderNumber[];
  manualPositionCorrectionConfiguration?: scanApi.ManualPositionCorrectionConfiguration;
  manualPositionCorrectionReferencePoints?: ScanCoordinate[];
}
